import type { MemberGender, StoredGenderCode } from '@/lib/gender';
import { isMemberGender } from '@/lib/gender';

export type MemberType = 'first_timer' | 'visitor' | 'member';
export type MemberStatus = 'active' | 'inactive' | 'transferred' | 'deceased';
export type Gender = MemberGender;
export type MaritalStatus =
  | 'single'
  | 'married'
  | 'separated'
  | 'divorced'
  | 'widowed'
  | 'cohabiting'
  | 'other';
export type ReceptionMethod =
  | 'profession'
  | 'confirmation'
  | 'transfer'
  | 'reaffirmation'
  | 'restoration'
  | 'birth'
  | 'other';
export type BaptismType = 'infant' | 'adult' | 'unknown';
export type MarriageType = 'customary' | 'ordinance' | 'civil' | 'both' | 'other';
export type MemberSourceKind =
  | 'gathering'
  | 'invited'
  | 'walk_in'
  | 'online'
  | 'social_media'
  | 'transfer'
  | 'other';
export type FamilyRelationship =
  | 'spouse'
  | 'parent'
  | 'child'
  | 'sibling'
  | 'grandparent'
  | 'grandchild'
  | 'uncle_aunt'
  | 'niece_nephew'
  | 'cousin'
  | 'guardian'
  | 'ward'
  | 'in_law'
  | 'other';

export interface MemberRecord {
  id: string;
  userId?: string | null;
  membershipNumber: string | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  maidenName: string | null;
  title: string | null;
  gender: StoredGenderCode | null;
  dateOfBirth: string | null;
  placeOfBirth: string | null;
  maritalStatus: MaritalStatus | null;
  isCommunicant: boolean | null;
  motherName: string | null;
  fatherName: string | null;
  occupation: string | null;
  employer: string | null;
  educationBackground: string | null;
  employmentBackground: string | null;
  nationalId: string | null;
  email: string | null;
  phone: string | null;
  alternatePhone: string | null;
  contactHidden?: boolean;
  secondaryHidden?: boolean;
  appConnectEmailSameAsEmail?: boolean;
  appConnectPhoneSameAsPhone?: boolean;
  appConnectEmail?: string | null;
  appConnectPhone?: string | null;
  effectiveAppConnectEmail?: string | null;
  effectiveAppConnectPhone?: string | null;
  address: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  postalAddress: string | null;
  postalCity: string | null;
  postalRegion: string | null;
  postalCountry: string | null;
  hometown: string | null;
  hometownRegion: string | null;
  memberType: MemberType;
  status: MemberStatus;
  receptionMethod: ReceptionMethod | null;
  formerChurch: string | null;
  transferDestinationChurch: string | null;
  transferDestinationLocation: string | null;
  transferDestinationBranchId: string | null;
  transferDestinationBranchName?: string | null;
  transferDate: string | null;
  joinedAt: string | null;
  baptismDate: string | null;
  baptismNumber: string | null;
  baptismPlace: string | null;
  baptismMinister: string | null;
  baptismType: BaptismType | null;
  confirmationDate: string | null;
  confirmationNumber: string | null;
  confirmationPlace: string | null;
  marriageDate: string | null;
  marriagePlace: string | null;
  marriageType: MarriageType | null;
  marriageMinister: string | null;
  spouseMemberId: string | null;
  spouseNameManual: string | null;
  spouseName?: string | null;
  emergencyContactMemberId: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: string | null;
  isDistantMember: boolean;
  hasDisability: boolean;
  disabilityNotes: string | null;
  dateOfDeath: string | null;
  burialDate: string | null;
  photoKey: string | null;
  photoUrl: string | null;
  notes: string | null;
  sourceKind: MemberSourceKind | null;
  sourceProgramId: string | null;
  sourceOccurrenceId: string | null;
  sourceInvitedByMemberId: string | null;
  sourceDetail: string | null;
  firstContactOn: string | null;
  sourceProgramName?: string | null;
  sourceOccurrenceDate?: string | null;
  invitedByName?: string | null;
}

export interface MemberFormState {
  membershipNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  maidenName: string;
  title: string;
  gender: Gender | null;
  dateOfBirth: string | null;
  placeOfBirth: string;
  maritalStatus: MaritalStatus | null;
  isCommunicant: boolean | null;
  motherName: string;
  fatherName: string;
  occupation: string;
  employer: string;
  educationBackground: string;
  employmentBackground: string;
  nationalId: string;
  email: string;
  phone: string | null;
  alternatePhone: string | null;
  address: string;
  city: string;
  region: string;
  country: string | null;
  postalAddress: string;
  postalCity: string;
  postalRegion: string;
  postalCountry: string | null;
  hometown: string;
  hometownRegion: string;
  memberType: MemberType | null;
  receptionMethod: ReceptionMethod | null;
  formerChurch: string;
  joinedAt: string | null;
  baptismDate: string | null;
  baptismNumber: string;
  baptismPlace: string;
  baptismMinister: string;
  baptismType: BaptismType | null;
  confirmationDate: string | null;
  confirmationNumber: string;
  confirmationPlace: string;
  marriageDate: string | null;
  marriagePlace: string;
  marriageType: MarriageType | null;
  marriageMinister: string;
  spouseMemberId: string | null;
  spouseNameManual: string;
  spouseIsMember: boolean;
  emergencyContactMemberId: string | null;
  emergencyContactIsMember: boolean;
  emergencyContactName: string;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: string;
  isDistantMember: boolean;
  hasDisability: boolean;
  disabilityNotes: string;
  photoKey: string | null;
  photoPreviewUrl: string | null;
  notes: string;
  appConnectEmailSameAsEmail: boolean;
  appConnectPhoneSameAsPhone: boolean;
  appConnectEmail: string;
  appConnectPhone: string | null;
  sourceKind: MemberSourceKind | null;
  sourceProgramId: string | null;
  sourceOccurrenceId: string | null;
  sourceInvitedByMemberId: string | null;
  invitedByIsMember: boolean;
  sourceDetail: string;
  firstContactOn: string | null;
}

export const emptyMemberForm = (overrides: Partial<MemberFormState> = {}): MemberFormState => ({
  membershipNumber: '',
  firstName: '',
  middleName: '',
  lastName: '',
  maidenName: '',
  title: '',
  gender: null,
  dateOfBirth: null,
  placeOfBirth: '',
  maritalStatus: null,
  isCommunicant: null,
  motherName: '',
  fatherName: '',
  occupation: '',
  employer: '',
  educationBackground: '',
  employmentBackground: '',
  nationalId: '',
  email: '',
  phone: null,
  alternatePhone: null,
  address: '',
  city: '',
  region: '',
  country: 'GH',
  postalAddress: '',
  postalCity: '',
  postalRegion: '',
  postalCountry: null,
  hometown: '',
  hometownRegion: '',
  memberType: 'member',
  receptionMethod: null,
  formerChurch: '',
  joinedAt: null,
  baptismDate: null,
  baptismNumber: '',
  baptismPlace: '',
  baptismMinister: '',
  baptismType: null,
  confirmationDate: null,
  confirmationNumber: '',
  confirmationPlace: '',
  marriageDate: null,
  marriagePlace: '',
  marriageType: null,
  marriageMinister: '',
  spouseMemberId: null,
  spouseNameManual: '',
  spouseIsMember: true,
  emergencyContactMemberId: null,
  emergencyContactIsMember: true,
  emergencyContactName: '',
  emergencyContactPhone: null,
  emergencyContactRelationship: '',
  isDistantMember: false,
  hasDisability: false,
  disabilityNotes: '',
  photoKey: null,
  photoPreviewUrl: null,
  notes: '',
  appConnectEmailSameAsEmail: true,
  appConnectPhoneSameAsPhone: true,
  appConnectEmail: '',
  appConnectPhone: null,
  sourceKind: null,
  sourceProgramId: null,
  sourceOccurrenceId: null,
  sourceInvitedByMemberId: null,
  invitedByIsMember: true,
  sourceDetail: '',
  firstContactOn: null,
  ...overrides,
});

export const joinRequestToMemberForm = (
  request: {
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    gender: string | null;
    dateOfBirth: string | null;
    userEmail: string;
    userPhone: string | null;
  },
  options: { country?: string | null; firstContactOn?: string | null } = {},
): MemberFormState => {
  const email = request.email?.trim() || request.userEmail;
  const phone = request.phone ?? request.userPhone;
  return emptyMemberForm({
    firstName: request.firstName,
    lastName: request.lastName,
    email,
    phone,
    gender: isMemberGender(request.gender) ? request.gender : null,
    dateOfBirth: request.dateOfBirth,
    country: options.country ?? 'GH',
    memberType: 'visitor',
    sourceKind: 'online',
    firstContactOn: options.firstContactOn ?? null,
    appConnectEmailSameAsEmail: true,
    appConnectPhoneSameAsPhone: true,
    appConnectEmail: email,
    appConnectPhone: phone,
  });
};

export const memberToForm = (member: MemberRecord): MemberFormState =>
  emptyMemberForm({
    membershipNumber: member.membershipNumber ?? '',
    firstName: member.firstName,
    middleName: member.middleName ?? '',
    lastName: member.lastName,
    maidenName: member.maidenName ?? '',
    title: member.title ?? '',
    gender: isMemberGender(member.gender) ? member.gender : null,
    dateOfBirth: member.dateOfBirth,
    placeOfBirth: member.placeOfBirth ?? '',
    maritalStatus:
      member.maritalStatus === 'cohabiting' ? 'other' : member.maritalStatus,
    isCommunicant: member.isCommunicant,
    motherName: member.motherName ?? '',
    fatherName: member.fatherName ?? '',
    occupation: member.occupation ?? '',
    employer: member.employer ?? '',
    educationBackground: member.educationBackground ?? '',
    employmentBackground: member.employmentBackground ?? '',
    nationalId: member.nationalId ?? '',
    email: member.email ?? '',
    phone: member.phone,
    alternatePhone: member.alternatePhone,
    address: member.address ?? '',
    city: member.city ?? '',
    region: member.region ?? '',
    country: member.country,
    postalAddress: member.postalAddress ?? '',
    postalCity: member.postalCity ?? '',
    postalRegion: member.postalRegion ?? '',
    postalCountry: member.postalCountry,
    hometown: member.hometown ?? '',
    hometownRegion: member.hometownRegion ?? '',
    memberType: member.memberType,
    receptionMethod: member.receptionMethod,
    formerChurch: member.formerChurch ?? '',
    joinedAt: member.joinedAt,
    baptismDate: member.baptismDate,
    baptismNumber: member.baptismNumber ?? '',
    baptismPlace: member.baptismPlace ?? '',
    baptismMinister: member.baptismMinister ?? '',
    baptismType: member.baptismType,
    confirmationDate: member.confirmationDate,
    confirmationNumber: member.confirmationNumber ?? '',
    confirmationPlace: member.confirmationPlace ?? '',
    marriageDate: member.marriageDate,
    marriagePlace: member.marriagePlace ?? '',
    marriageType: member.marriageType,
    marriageMinister: member.marriageMinister ?? '',
    spouseMemberId: member.spouseMemberId,
    spouseNameManual: member.spouseNameManual ?? '',
    spouseIsMember: Boolean(member.spouseMemberId),
    emergencyContactMemberId: member.emergencyContactMemberId,
    emergencyContactIsMember: Boolean(member.emergencyContactMemberId),
    emergencyContactName: member.emergencyContactName ?? '',
    emergencyContactPhone: member.emergencyContactPhone,
    emergencyContactRelationship: member.emergencyContactRelationship ?? '',
    isDistantMember: member.isDistantMember,
    hasDisability: member.hasDisability,
    disabilityNotes: member.disabilityNotes ?? '',
    photoKey: member.photoKey,
    photoPreviewUrl: member.photoUrl,
    notes: member.notes ?? '',
    appConnectEmailSameAsEmail: member.appConnectEmailSameAsEmail ?? true,
    appConnectPhoneSameAsPhone: member.appConnectPhoneSameAsPhone ?? true,
    appConnectEmail: member.appConnectEmail ?? '',
    appConnectPhone: member.appConnectPhone ?? null,
    sourceKind: member.sourceKind,
    sourceProgramId: member.sourceProgramId,
    sourceOccurrenceId: member.sourceOccurrenceId,
    sourceInvitedByMemberId: member.sourceInvitedByMemberId,
    invitedByIsMember: Boolean(member.sourceInvitedByMemberId),
    sourceDetail: member.sourceDetail ?? '',
    firstContactOn: member.firstContactOn,
  });

export const formToPayload = (form: MemberFormState): Record<string, unknown> => ({
  membershipNumber: form.membershipNumber.trim() || null,
  firstName: form.firstName.trim(),
  middleName: form.middleName.trim() || null,
  lastName: form.lastName.trim(),
  maidenName: form.maidenName.trim() || null,
  title: form.title.trim() || null,
  gender: form.gender,
  dateOfBirth: form.dateOfBirth,
  placeOfBirth: form.placeOfBirth.trim() || null,
  maritalStatus: form.maritalStatus,
  isCommunicant: form.isCommunicant,
  motherName: form.motherName.trim() || null,
  fatherName: form.fatherName.trim() || null,
  occupation: form.occupation.trim() || null,
  employer: form.employer.trim() || null,
  educationBackground: form.educationBackground.trim() || null,
  employmentBackground: form.employmentBackground.trim() || null,
  nationalId: form.nationalId.trim() || null,
  email: form.email.trim() || null,
  phone: form.phone || null,
  alternatePhone: form.alternatePhone || null,
  address: form.address.trim() || null,
  city: form.city.trim() || null,
  region: form.region.trim() || null,
  country: form.country,
  postalAddress: form.postalAddress.trim() || null,
  postalCity: form.postalCity.trim() || null,
  postalRegion: form.postalRegion.trim() || null,
  postalCountry: form.postalCountry,
  hometown: form.hometown.trim() || null,
  hometownRegion: form.hometownRegion.trim() || null,
  memberType: form.memberType ?? 'member',
  receptionMethod: form.receptionMethod,
  formerChurch: form.formerChurch.trim() || null,
  joinedAt: form.joinedAt,
  baptismDate: form.baptismDate,
  baptismNumber: form.baptismNumber.trim() || null,
  baptismPlace: form.baptismPlace.trim() || null,
  baptismMinister: form.baptismMinister.trim() || null,
  baptismType: form.baptismType,
  confirmationDate: form.confirmationDate,
  confirmationNumber: form.confirmationNumber.trim() || null,
  confirmationPlace: form.confirmationPlace.trim() || null,
  marriageDate: form.maritalStatus === 'married' ? form.marriageDate : null,
  marriagePlace: form.maritalStatus === 'married' ? form.marriagePlace.trim() || null : null,
  marriageType: form.maritalStatus === 'married' ? form.marriageType : null,
  marriageMinister: form.maritalStatus === 'married' ? form.marriageMinister.trim() || null : null,
  spouseMemberId:
    form.maritalStatus === 'married' && form.spouseIsMember ? form.spouseMemberId : null,
  spouseNameManual:
    form.maritalStatus === 'married' && !form.spouseIsMember
      ? form.spouseNameManual.trim() || null
      : null,
  emergencyContactMemberId: form.emergencyContactIsMember
    ? form.emergencyContactMemberId
    : null,
  emergencyContactName:
    !form.emergencyContactIsMember ? form.emergencyContactName.trim() || null : null,
  emergencyContactPhone:
    !form.emergencyContactIsMember ? form.emergencyContactPhone || null : null,
  emergencyContactRelationship: form.emergencyContactRelationship.trim() || null,
  isDistantMember: form.isDistantMember,
  hasDisability: form.hasDisability,
  disabilityNotes: form.disabilityNotes.trim() || null,
  photoKey: form.photoKey,
  notes: form.notes.trim() || null,
  appConnectEmailSameAsEmail: form.appConnectEmailSameAsEmail,
  appConnectPhoneSameAsPhone: form.appConnectPhoneSameAsPhone,
  appConnectEmail: form.appConnectEmailSameAsEmail
    ? form.email.trim().toLowerCase() || null
    : form.appConnectEmail.trim().toLowerCase() || null,
  appConnectPhone: form.appConnectPhoneSameAsPhone
    ? form.phone || null
    : form.appConnectPhone || null,
  sourceKind: form.sourceKind,
  sourceProgramId: form.sourceKind === 'gathering' ? form.sourceProgramId : null,
  sourceOccurrenceId: form.sourceKind === 'gathering' ? form.sourceOccurrenceId : null,
  sourceInvitedByMemberId:
    form.sourceKind === 'invited' && form.invitedByIsMember
      ? form.sourceInvitedByMemberId
      : null,
  sourceDetail:
    form.sourceKind === 'invited' && !form.invitedByIsMember
      ? form.sourceDetail.trim() || null
      : form.sourceKind === 'other'
        ? form.sourceDetail.trim() || null
        : null,
  firstContactOn: form.firstContactOn,
});

export const FAMILY_RELATIONSHIPS: FamilyRelationship[] = [
  'spouse',
  'parent',
  'child',
  'sibling',
  'grandparent',
  'grandchild',
  'uncle_aunt',
  'niece_nephew',
  'cousin',
  'guardian',
  'ward',
  'in_law',
  'other',
];

export const MARITAL_STATUS_OPTIONS: MaritalStatus[] = [
  'single',
  'married',
  'separated',
  'divorced',
  'widowed',
  'other',
];
